import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { devWarn } from "@/lib/debug";
import { toast } from "sonner";

/**
 * useMismatchCorrections — encapsulates all fetch / submit / decide / cancel
 * flows for a given SPB's correction thread. Keeps the panel focused on
 * presentation.
 */
export default function useMismatchCorrections(spb, onSpbUpdated) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchList = useCallback(async () => {
    if (!spb?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/spb/${spb.id}/mismatch/corrections`);
      setItems(r.data || []);
    } catch (e) {
      // Silent for the user — panel is optional context. Dev-only warn so
      // regressions are still visible in `yarn start` console.
      devWarn("[useMismatchCorrections] fetch failed:", e);
    } finally {
      setLoading(false);
    }
  }, [spb?.id]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const submit = async ({ proposed, reason }) => {
    const changes = {};
    Object.entries(proposed).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      changes[k] = k === "jumlah" ? Number(v) : v;
    });
    if (!Object.keys(changes).length) {
      toast.error("Isi minimal 1 field yang diusulkan");
      return false;
    }
    if (reason.trim().length < 3) {
      toast.error("Alasan wajib diisi (minimal 3 karakter)");
      return false;
    }
    setSubmitting(true);
    try {
      await api.post(`/spb/${spb.id}/mismatch/corrections`, {
        proposed_changes: changes, reason: reason.trim(),
      });
      toast.success("Koreksi diajukan — menunggu approval Super Admin");
      await fetchList();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const decide = async ({ id, action, note }) => {
    setSubmitting(true);
    try {
      await api.post(`/mismatch/corrections/${id}/${action}`, { note });
      toast.success(action === "approve"
        ? "Koreksi disetujui — SPB ter-update & diverifikasi"
        : "Koreksi ditolak");
      await fetchList();
      onSpbUpdated && onSpbUpdated();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const cancelDraft = async (cid) => {
    if (!window.confirm("Batalkan draft koreksi ini?")) return;
    try {
      await api.delete(`/mismatch/corrections/${cid}`);
      toast.success("Draft koreksi dibatalkan");
      await fetchList();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return { items, loading, submitting, submit, decide, cancelDraft };
}
